import { useState, createContext, useContext } from 'react';
import { cn } from '../../utils/cn';

// Context for tabs state
const TabsContext = createContext(null);

// Main Tabs container
export function Tabs({ defaultValue, value, onValueChange, children, className }) {
  const [internalValue, setInternalValue] = useState(defaultValue);

  const currentValue = value !== undefined ? value : internalValue;

  const handleValueChange = (newValue) => {
    if (value === undefined) {
      setInternalValue(newValue);
    }
    onValueChange?.(newValue);
  };

  return (
    <TabsContext.Provider value={{ value: currentValue, onValueChange: handleValueChange }}>
      <div className={cn('w-full', className)}>
        {children}
      </div>
    </TabsContext.Provider>
  );
}

// Tab list (container for tab triggers)
export function TabsList({ children, className }) {
  return (
    <div
      className={cn(
        'flex border-b border-border bg-background relative',
        className
      )}
      role="tablist"
    >
      {children}
    </div>
  );
}

// Individual tab trigger/button
export function TabsTrigger({ value, children, className, disabled = false }) {
  const context = useContext(TabsContext);

  if (!context) {
    throw new Error('TabsTrigger must be used within a Tabs component');
  }

  const isActive = context.value === value;

  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      disabled={disabled}
      onClick={() => !disabled && context.onValueChange(value)}
      className={cn(
        'px-4 py-3 text-sm font-medium transition-colors',
        'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background',
        isActive
          ? 'text-primary border-b-[3px] border-b-primary'
          : 'text-text-secondary hover:text-text-primary border-b-[3px] border-b-transparent',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
    >
      {children}
    </button>
  );
}

// Tab content panel
export function TabsContent({ value, children, className }) {
  const context = useContext(TabsContext);

  if (!context) {
    throw new Error('TabsContent must be used within a Tabs component');
  }

  if (context.value !== value) {
    return null;
  }

  return (
    <div
      role="tabpanel"
      className={cn('py-4', className)}
    >
      {children}
    </div>
  );
}

// Export all components
export default { Tabs, TabsList, TabsTrigger, TabsContent };
